import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  createAdmissionFormSetting,
  getAdmissionFormSetting,
  updateAdmissionFormSetting,
} from '../api/admissionFormSettingApi'
import { fetchCompanyOptions } from '../../../../api/filterOptionApi/companyFilterApi'
import { fetchBranchOptions } from '../../../../api/filterOptionApi/branchFilterApi'
import { loadMasterOptions } from '../../students/api/mastersApi'

const buildDefaultSettings = () => ({
  courseDetails: {
    course: { enabled: true, label: 'Course', mandatory: true },
    branch: { enabled: true, label: 'Branch', mandatory: false },
    batch: { enabled: true, label: 'Batch', mandatory: true },
    subjects: { enabled: false, label: 'Subjects', mandatory: false },
  },
  studentDetails: {
    name: { enabled: true, label: 'Name', mandatory: true },
    dob: { enabled: true, label: 'D.O.B.', mandatory: false },
    mobile: { enabled: true, label: 'Mobile', mandatory: true },
    email: { enabled: true, label: 'Email', mandatory: false },
    gender: { enabled: true, label: 'Gender', mandatory: false },
    landline: { enabled: false, label: 'Landline', mandatory: false },
    address: { enabled: true, label: 'Address', mandatory: true },
    remarks: { enabled: true, label: 'Remark', mandatory: false },
  },
  guardianDetails: {
    name: { enabled: true, label: 'Name', mandatory: false },
    email: { enabled: true, label: 'Email ID', mandatory: false },
    mobile: { enabled: true, label: 'Mobile', mandatory: true },
    landline: { enabled: false, label: 'Landline', mandatory: false },
    occupation: { enabled: true, label: 'Occupation', mandatory: false },
    income: { enabled: true, label: 'Income', mandatory: false },
    relation: { enabled: true, label: 'Relation', mandatory: false },
  },
  guardian1Details: {
    name: { enabled: true, label: 'Name', mandatory: false },
    email: { enabled: true, label: 'Email ID', mandatory: false },
    mobile: { enabled: true, label: 'Mobile', mandatory: false },
    landline: { enabled: false, label: 'Landline', mandatory: false },
    occupation: { enabled: false, label: 'Occupation', mandatory: false },
    income: { enabled: false, label: 'Income', mandatory: false },
    relation: { enabled: false, label: 'Relation', mandatory: false },
  },
  schoolCollegeDetails: {
    school: { enabled: true, label: 'School', mandatory: false },
    board: { enabled: false, label: 'Board', mandatory: false },
    course: { enabled: true, label: 'Course', mandatory: false },
    marks: { enabled: true, label: 'Marks', mandatory: false },
    status: { enabled: true, label: 'Status', mandatory: false },
    medium: { enabled: false, label: 'Medium', mandatory: false },
    termsConditions: { enabled: true, label: 'TermsConditions', mandatory: true },
  },
})

const emptyForm = {
  name: '',
  company: '',
  branch: '',
  status: 'Active',
  settings: buildDefaultSettings(),
}

export function useAdmissionFormSettingEntryScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useMemo(() => new URLSearchParams(location.search), [location.search])
  const entryId = params.get('id')

  const [masterOptions, setMasterOptions] = useState({ institutes: [], branches: [] })
  const [form, setForm] = useState(emptyForm)
  const [isLoading, setIsLoading] = useState(Boolean(entryId))
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    let isMounted = true
    async function loadOptions() {
      let comps = []
      let branches = []

      try {
        const compRes = await fetchCompanyOptions('', 1, 100)
        comps = compRes?.items?.map((item) => item.label) || []
      } catch {
        comps = []
      }
      if (comps.length === 0) {
        try {
          const masterData = await loadMasterOptions(['institutes'])
          comps = masterData.institutes || []
        } catch {
          comps = []
        }
      }

      try {
        const brRes = await fetchBranchOptions('', 1, 100)
        branches = brRes?.items?.map((item) => item.label) || []
      } catch {
        branches = []
      }
      if (branches.length === 0) {
        try {
          const masterData = await loadMasterOptions(['branches'])
          branches = masterData.branches || []
        } catch {
          branches = []
        }
      }

      if (isMounted) {
        setMasterOptions({ institutes: comps, branches })
        if (!entryId) {
          setForm((current) => ({
            ...current,
            company: current.company || comps[0] || '',
            branch: current.branch || branches[0] || '',
          }))
        }
      }
    }

    loadOptions()
    return () => {
      isMounted = false
    }
  }, [entryId])

  useEffect(() => {
    const loadEntry = async () => {
      if (!entryId) {
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      try {
        const setting = await getAdmissionFormSetting(entryId)
        if (setting) {
          setForm({
            id: setting.id,
            name: setting.name || '',
            company: setting.company || '',
            branch: setting.branch || '',
            status: setting.status || 'Active',
            settings: setting.settings || buildDefaultSettings(),
          })
        }
      } catch (requestError) {
        setError('Unable to load admission form setting details.')
      } finally {
        setIsLoading(false)
      }
    }

    loadEntry()
  }, [entryId])

  const patchField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }))
  }

  const updateSettingFlag = (group, key, field, value) => {
    setForm((current) => ({
      ...current,
      settings: {
        ...current.settings,
        [group]: {
          ...current.settings[group],
          [key]: {
            ...current.settings[group][key],
            [field]: value,
          },
        },
      },
    }))
  }

  const validate = () => {
    if (!form.name || !form.name.trim()) {
      setError('Form setting name is required.')
      return false
    }
    return true
  }

  const handleSave = async () => {
    if (!validate()) return

    setIsSaving(true)
    setError('')

    try {
      const payload = {
        name: form.name.trim(),
        company: form.company,
        branch: form.branch,
        status: form.status,
        updatedAt: new Date().toISOString().slice(0, 10),
        settings: form.settings,
      }

      if (entryId) {
        await updateAdmissionFormSetting(entryId, payload)
        setSuccess('Form setting updated successfully.')
      } else {
        await createAdmissionFormSetting(payload)
        setSuccess('Form setting created successfully.')
      }

      setTimeout(() => {
        navigate('/admission/admission-form-setting/list')
      }, 600)
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail
      const msg = typeof detail === 'object' && detail?.message
        ? detail.message
        : typeof detail === 'string'
        ? detail
        : requestError?.response?.data?.message || 'Failed to save the admission form setting.'
      setError(msg)
    } finally {
      setIsSaving(false)
    }
  }

  return {
    state: {
      form,
      isLoading,
      isSaving,
      error,
      success,
      isEditMode: Boolean(entryId),
      options: {
        companyOptions: masterOptions.institutes || [],
        branchOptions: masterOptions.branches || [],
      },
    },
    actions: {
      patchField,
      updateSettingFlag,
      handleSave,
    },
  }
}
